/* ──────────────────────────────────────────────────────────────────────────
 * Operational digital twin — simulation engine (brief §5).
 *
 * Answers what-if questions over the current geospatial state:
 *   "What happens if this road closes?"
 *   "What happens if crowd density increases in Sector X?"
 *   "What is the nearest viable evacuation route?"
 *   "What resources are available around this incident?"
 *
 * THE RULE THIS FILE ENFORCES: a simulation result is never mistakable for an
 * observation. Every output carries `isSimulation: true`, a TRUST.SIMULATED
 * provenance, and the assumptions it ran under. The UI renders simulated
 * geometry dashed and in a distinct hue, and labels every figure PROJECTED.
 *
 * Why that matters here more than usual: these outputs look exactly like
 * operational readings — a sector occupancy, an evacuation route, a resource
 * list. In a command room, a projection mistaken for a reading is how people
 * make decisions on things that never happened.
 *
 * The models are transparent and crude on purpose. Redistribution is a simple
 * proportional spill to adjacent sectors; it is not a pedestrian-dynamics
 * model. Each result states its own method so nobody mistakes it for one.
 * ────────────────────────────────────────────────────────────────────────── */

import { TRUST, SOURCE_KIND, CLASSIFICATION } from "../data/provenance";
import {
  SECTORS, ROUTES, FACILITIES, STATUS_THRESHOLDS, sectorStatus,
} from "../data/kumbhGeo";

/* Great-circle distance in km. Haversine — accurate enough at these scales
 * and dependency-free. */
export function distanceKm([lng1, lat1], [lng2, lat2]) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/* Rough walking-time estimate. 4.5 km/h is an unimpeded adult pace; a dense
 * pilgrim crowd moves far slower, so this is optimistic and says so. */
const WALK_KMH = 4.5;
const walkMinutes = (km) => Math.round((km / WALK_KMH) * 60);

const simProvenance = (method) => ({
  source: SOURCE_KIND.SIMULATION,
  trust: TRUST.SIMULATED,
  confidence: null,
  owner: "InQAi simulation",
  classification: CLASSIFICATION.INTERNAL,
  updatedAt: new Date().toISOString(),
  note: `Projected result, not an observation. Method: ${method}`,
});

export const SCENARIO = {
  CLOSE_ROUTE: "closeRoute",
  DENSITY_SURGE: "densitySurge",
  EVACUATION: "evacuation",
  RESOURCES_NEAR: "resourcesNear",
};

/* ── 1. What happens if this route closes? ─────────────────────────────
 * Finds sectors that depended on the route, and the nearest alternative of
 * the same kind. Dependency is proximity-based: any sector whose centre is
 * within `reachKm` of any vertex of the route. That is a coarse proxy for a
 * real network analysis and is labelled as such. */
export function simulateRouteClosure(routeId, { reachKm = 2.5 } = {}) {
  const route = ROUTES.find((r) => r.id === routeId);
  if (!route) {
    return { ok: false, reason: `Route ${routeId} not found` };
  }

  const affected = SECTORS.filter((s) =>
    route.coords.some((c) => distanceKm(c, s.centre) <= reachKm)
  );

  // Alternatives: same layer, excluding the closed one, ranked by how close
  // they come to the closed route's midpoint.
  const mid = route.coords[Math.floor(route.coords.length / 2)];
  const alternatives = ROUTES
    .filter((r) => r.id !== routeId && r.layer === route.layer)
    .map((r) => {
      const best = Math.min(...r.coords.map((c) => distanceKm(c, mid)));
      return { id: r.id, name: r.name, layer: r.layer, detourKm: Number(best.toFixed(2)) };
    })
    .sort((a, b) => a.detourKm - b.detourKm);

  // Diversion routes are contingency geometry — they exist precisely for this.
  const diversions = ROUTES
    .filter((r) => r.layer === "diversionRoutes")
    .map((r) => ({ id: r.id, name: r.name, geometry: r.coords }));

  return {
    ok: true,
    isSimulation: true,
    scenario: SCENARIO.CLOSE_ROUTE,
    subject: { id: route.id, name: route.name, layer: route.layer },
    affectedSectors: affected.map((s) => ({
      id: s.id, name: s.name, current: s.current, status: sectorStatus(s),
    })),
    alternatives: alternatives.slice(0, 3),
    diversions,
    geometry: route.coords,
    assumptions: [
      `A sector is treated as affected if its centre lies within ${reachKm} km of the route.`,
      "Proximity stands in for network dependency — no road-graph routing is performed.",
      "Alternatives are ranked by straight-line distance to the closed route's midpoint, not by travel time.",
    ],
    provenance: simProvenance("proximity-based dependency, straight-line ranking"),
  };
}

/* ── 2. What happens if density increases in a sector? ─────────────────
 * Applies an increase, then spills the overflow above capacity to the
 * nearest sectors that still have headroom — a crude proxy for people being
 * turned away and walking somewhere else. */
export function simulateDensitySurge(sectorId, { increasePct = 25 } = {}) {
  const target = SECTORS.find((s) => s.id === sectorId);
  if (!target) return { ok: false, reason: `Sector ${sectorId} not found` };

  const projected = SECTORS.map((s) => ({
    id: s.id,
    name: s.name,
    centre: s.centre,
    capacity: s.capacity,
    before: s.current,
    after: s.current,
  }));

  const t = projected.find((s) => s.id === sectorId);
  t.after = Math.round(t.before * (1 + increasePct / 100));

  // Overflow above capacity spills to the nearest sectors with headroom.
  let overflow = Math.max(0, t.after - t.capacity);
  if (overflow > 0) {
    t.after = t.capacity;
    const neighbours = projected
      .filter((s) => s.id !== sectorId)
      .map((s) => ({ s, d: distanceKm(target.centre, s.centre) }))
      .sort((a, b) => a.d - b.d);

    for (const { s } of neighbours) {
      if (overflow <= 0) break;
      const headroom = Math.max(0, s.capacity - s.after);
      const take = Math.min(headroom, overflow);
      s.after += take;
      overflow -= take;
    }
  }

  const rows = projected.map((s) => {
    const ratioBefore = s.before / s.capacity;
    const ratioAfter = s.after / s.capacity;
    const band = (r) =>
      r >= STATUS_THRESHOLDS.red ? "red" : r >= STATUS_THRESHOLDS.amber ? "amber" : "green";
    return {
      id: s.id, name: s.name, centre: s.centre, capacity: s.capacity,
      before: s.before, after: s.after,
      statusBefore: band(ratioBefore), statusAfter: band(ratioAfter),
      changed: band(ratioBefore) !== band(ratioAfter),
      ratioAfterPct: Math.round(ratioAfter * 100),
    };
  });

  return {
    ok: true,
    isSimulation: true,
    scenario: SCENARIO.DENSITY_SURGE,
    subject: { id: target.id, name: target.name },
    increasePct,
    rows,
    newlyCritical: rows.filter((r) => r.changed && r.statusAfter === "red"),
    unabsorbed: overflow,
    assumptions: [
      `${increasePct}% increase applied to ${target.id} only.`,
      "Overflow above capacity spills to the nearest sectors with headroom, in distance order.",
      "No pedestrian-dynamics model: this is arithmetic redistribution, not a crowd simulation.",
      overflow > 0
        ? `${overflow.toLocaleString("en-IN")} people could not be absorbed anywhere — the theatre is at capacity in this scenario.`
        : "All overflow was absorbed by neighbouring sectors.",
    ],
    provenance: simProvenance("proportional spill to nearest sectors with headroom"),
  };
}

/* ── 3. Nearest viable evacuation route ────────────────────────────────
 * "Viable" here means: mapped as an evacuation route, and its entry point is
 * reachable. We report the distance to the route's nearest vertex and an
 * optimistic walking time. */
export function simulateEvacuation(fromCoord) {
  const evac = ROUTES.filter((r) => r.layer === "evacuationRoutes");
  if (!evac.length) {
    return { ok: false, reason: "No evacuation routes are mapped." };
  }

  const ranked = evac
    .map((r) => {
      let best = Infinity;
      let entryIdx = 0;
      r.coords.forEach((c, i) => {
        const d = distanceKm(fromCoord, c);
        if (d < best) { best = d; entryIdx = i; }
      });
      // Length from the entry point to the far end of the route.
      let along = 0;
      for (let i = entryIdx; i < r.coords.length - 1; i += 1) {
        along += distanceKm(r.coords[i], r.coords[i + 1]);
      }
      return {
        id: r.id, name: r.name,
        entryCoord: r.coords[entryIdx],
        toEntryKm: Number(best.toFixed(2)),
        alongRouteKm: Number(along.toFixed(2)),
        totalKm: Number((best + along).toFixed(2)),
        etaMinutes: walkMinutes(best + along),
        geometry: r.coords.slice(entryIdx),
      };
    })
    .sort((a, b) => a.totalKm - b.totalKm);

  return {
    ok: true,
    isSimulation: true,
    scenario: SCENARIO.EVACUATION,
    origin: fromCoord,
    best: ranked[0],
    alternatives: ranked.slice(1),
    assumptions: [
      "Distance to the route entry is straight-line, not walked — the real approach may be longer or blocked.",
      `Walking speed assumed ${WALK_KMH} km/h, which is an unimpeded adult pace. A dense crowd moves far slower, so the ETA is optimistic.`,
      "Route viability is not verified against current incidents, closures or capacity.",
      "These are illustrative lines, NOT the district's approved evacuation plan.",
    ],
    provenance: simProvenance("nearest-vertex entry, straight-line approach, fixed walking speed"),
  };
}

/* ── 4. What resources are available around this incident? ─────────────
 * A radius query over facilities. Honest about being a straight-line radius
 * rather than a travel-time isochrone. */
export function simulateResourcesNear(coord, { radiusKm = 3, layers = null } = {}) {
  const pool = layers ? FACILITIES.filter((f) => layers.includes(f.layer)) : FACILITIES;

  const found = pool
    .map((f) => ({
      id: f.id, name: f.name, layer: f.layer, coord: f.coord,
      classification: f.provenance?.classification,
      trust: f.provenance?.trust,
      distanceKm: Number(distanceKm(coord, f.coord).toFixed(2)),
      etaMinutes: walkMinutes(distanceKm(coord, f.coord)),
    }))
    .filter((f) => f.distanceKm <= radiusKm)
    .sort((a, b) => a.distanceKm - b.distanceKm);

  const byLayer = found.reduce((acc, f) => {
    (acc[f.layer] = acc[f.layer] || []).push(f);
    return acc;
  }, {});

  return {
    ok: true,
    isSimulation: true,
    scenario: SCENARIO.RESOURCES_NEAR,
    origin: coord,
    radiusKm,
    count: found.length,
    found,
    byLayer,
    assumptions: [
      `Straight-line radius of ${radiusKm} km — not a travel-time isochrone, and it ignores the river, barricades and one-way flows.`,
      "Availability is NOT modelled: a listed facility may be at capacity, closed or already committed.",
      "Facility positions are themselves largely simulated — see the geography notes.",
    ],
    provenance: simProvenance("straight-line radius over the facility catalogue"),
  };
}

/* Scenario catalogue for the UI, so adding a scenario is one entry. */
export const SCENARIOS = [
  {
    key: SCENARIO.CLOSE_ROUTE,
    label: { en: "Close a route", mr: "मार्ग बंद करा" },
    question: { en: "What happens if this road closes?", mr: "हा मार्ग बंद झाल्यास काय होईल?" },
    needs: "route",
  },
  {
    key: SCENARIO.DENSITY_SURGE,
    label: { en: "Density surge", mr: "गर्दी वाढ" },
    question: { en: "What if crowd density increases in a sector?", mr: "सेक्टरमध्ये गर्दी वाढल्यास?" },
    needs: "sector",
  },
  {
    key: SCENARIO.EVACUATION,
    label: { en: "Nearest evacuation", mr: "जवळचे निर्वासन" },
    question: { en: "What is the nearest viable evacuation route?", mr: "जवळचा व्यवहार्य निर्वासन मार्ग कोणता?" },
    needs: "point",
  },
  {
    key: SCENARIO.RESOURCES_NEAR,
    label: { en: "Resources nearby", mr: "जवळची संसाधने" },
    question: { en: "What resources are available around this incident?", mr: "या घटनेभोवती कोणती संसाधने आहेत?" },
    needs: "point",
  },
];
